import { trackEvent } from '@/lib/analytics'

export type ContactLinkKind = 'call' | 'whatsapp'

/** Seller dashboards call their own leads — those taps are not QBC leads. */
const EXCLUDED_PATH_PREFIXES = ['/dashboard', '/my-property', '/my-inquiries', '/my-site-visits']
const DEDUPE_MS = 1500

export function contactLinkKind(href: string): ContactLinkKind | null {
  const value = href.trim().toLowerCase()
  if (value.startsWith('tel:')) return 'call'
  if (
    value.startsWith('whatsapp:') ||
    /^https?:\/\/(wa\.me|api\.whatsapp\.com|web\.whatsapp\.com|chat\.whatsapp\.com)\//.test(value)
  ) {
    return 'whatsapp'
  }
  return null
}

function propertyIdFromPath(pathname: string): number | undefined {
  const match = /^\/property\/(\d+)/.exec(pathname)
  const id = match ? Number(match[1]) : NaN
  return Number.isFinite(id) && id > 0 ? id : undefined
}

function linkLocation(anchor: Element): string {
  const tagged = anchor.closest('[data-track-location]')?.getAttribute('data-track-location')
  if (tagged) return tagged
  if (anchor.closest('footer')) return 'footer'
  if (anchor.closest('header')) return 'header'
  return 'page'
}

/**
 * One document listener covers every tel: / WhatsApp link (header, footer, contact, property,
 * floating button…) so GA4 gets `call_click` / `whatsapp_click` without wiring each component.
 */
export function initContactLinkTracking(): void {
  if (typeof document === 'undefined') return
  const last = new Map<string, number>()

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target
      if (!(target instanceof Element)) return
      const anchor = target.closest('a[href]')
      if (!anchor) return
      const kind = contactLinkKind(anchor.getAttribute('href') || '')
      if (!kind) return

      const pathname = window.location.pathname
      if (EXCLUDED_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix))) return

      const now = Date.now()
      const key = `${kind}:${pathname}`
      const previous = last.get(key)
      if (previous != null && now - previous < DEDUPE_MS) return
      last.set(key, now)

      trackEvent(kind === 'call' ? 'call_click' : 'whatsapp_click', {
        link_location: linkLocation(anchor),
        page_path: pathname,
        property_id: propertyIdFromPath(pathname),
      })
    },
    { capture: true },
  )
}

export function trackLead(formName: string, params?: Record<string, string | number | undefined>): void {
  trackEvent('generate_lead', { form_name: formName, ...params })
}
